import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { User, Package, LogOut, Camera, Trash2, Loader2, ShoppingBag, ShoppingCart, ChevronRight, Store, Bell, Heart, MessageCircle, Check, X, Gift, Edit2, MessageSquare, ArrowLeft, Wallet, CreditCard, Plus, Shield, Lock, Settings, RotateCcw, History, Mail, Star, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import LoyaltyCard from "@/components/LoyaltyCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Account = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, getAuthHeader, loading: authLoading, refreshUser } = useAuth();
  const { t } = useLanguage();
  const { addToCart } = useCart();
  const [orders, setOrders] = useState([]);
  const [addingToCart, setAddingToCart] = useState({});
  const [downloadingInvoice, setDownloadingInvoice] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [userPosts, setUserPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [walletTab, setWalletTab] = useState('cards');
  const [orderTab, setOrderTab] = useState('history');
  const [notificationTab, setNotificationTab] = useState('all');
  const fileInputRef = useRef(null);
  
  // Customer details state
  const [customerDetails, setCustomerDetails] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom'
  });
  const [editingDetails, setEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  
  // Payment methods state - fetched from Stripe in production
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [editingCard, setEditingCard] = useState(null);
  
  // Payment preferences state
  const [paymentPreferences, setPaymentPreferences] = useState({
    savePaymentDetails: true,
    useDefaultAtCheckout: true
  });

  // Remove payment method
  const removePaymentMethod = (id) => {
    if (window.confirm('Are you sure you want to remove this payment method?')) {
      setPaymentMethods(paymentMethods.filter(pm => pm.id !== id));
      toast.success('Payment method removed');
    }
  };

  // Set default payment method
  const setDefaultPaymentMethod = (id) => {
    setPaymentMethods(paymentMethods.map(pm => ({
      ...pm,
      isDefault: pm.id === id
    })));
    toast.success('Default payment method updated');
  };

  // Load customer details from user
  useEffect(() => {
    if (user) {
      setCustomerDetails({
        firstName: user.first_name || user.name?.split(' ')[0] || '',
        lastName: user.last_name || user.name?.split(' ').slice(1).join(' ') || '',
        address: user.address || '',
        city: user.city || '',
        postcode: user.postcode || '',
        country: user.country || 'United Kingdom'
      });
    }
  }, [user]);

  // Save customer details
  const saveCustomerDetails = async () => {
    setSavingDetails(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        first_name: customerDetails.firstName,
        last_name: customerDetails.lastName,
        address: customerDetails.address,
        city: customerDetails.city,
        postcode: customerDetails.postcode,
        country: customerDetails.country
      }, {
        headers: getAuthHeader()
      });
      toast.success('Details saved successfully');
      setEditingDetails(false);
      refreshUser();
    } catch (error) {
      toast.error('Failed to save details');
    } finally {
      setSavingDetails(false);
    }
  };

  // Fetch user's forum posts
  const fetchUserPosts = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/community/posts?user_id=${user.id}&limit=50`);
      if (response.data?.posts) {
        setUserPosts(response.data.posts);
      }
    } catch (error) {
      console.error("Error fetching user posts:", error);
    } finally {
      setLoadingPosts(false);
    }
  };

  // Delete a post
  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this topic?")) return;
    try {
      await axios.delete(`${API}/community/posts/${postId}`, {
        headers: getAuthHeader()
      });
      toast.success("Topic deleted successfully");
      fetchUserPosts();
    } catch (error) {
      toast.error("Failed to delete topic");
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: getAuthHeader()
      });
      setNotifications(response.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
      return;
    }

    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: getAuthHeader()
        });
        setOrders(response.data);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchOrders();
      fetchNotifications();
      fetchUserPosts();
    }
  }, [isAuthenticated, authLoading, navigate, getAuthHeader]);

  const markNotificationAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: getAuthHeader()
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: getAuthHeader()
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: getAuthHeader()
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'like':
        return <Heart className="h-4 w-4 text-red-500" fill="currentColor" />;
      case 'comment':
        return <MessageCircle className="h-4 w-4 text-blue-500" />;
      case 'private_message':
        return <Mail className="h-4 w-4 text-purple-500" />;
      case 'points_earned':
        return <Star className="h-4 w-4 text-yellow-500" fill="currentColor" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large. Maximum size is 5MB.");
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/auth/profile/upload-image`, formData, {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data"
        }
      });
      
      if (response.data.success) {
        toast.success("Profile image updated!");
        // Refresh user data to get new profile image
        if (refreshUser) {
          await refreshUser();
        }
        // Force re-render by updating local state
        window.location.reload();
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!user?.profile_image) return;
    
    setUploadingImage(true);
    try {
      await axios.delete(`${API}/auth/profile/image`, {
        headers: getAuthHeader()
      });
      toast.success("Profile image removed");
      window.location.reload();
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to remove image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleDownloadInvoice = async (orderId) => {
    setDownloadingInvoice(prev => ({ ...prev, [orderId]: true }));
    try {
      const response = await axios.get(
        `${API}/orders/invoice/${orderId}`,
        { 
          responseType: 'blob',
          headers: getAuthHeader()
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${orderId.slice(0, 8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice. Please try again.");
    } finally {
      setDownloadingInvoice(prev => ({ ...prev, [orderId]: false }));
    }
  };

  if (authLoading || !isAuthenticated) {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-secondary w-1/3" />
            <div className="h-32 bg-secondary" />
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
      case "paid":
        return "text-green-600";
      case "pending":
        return "text-yellow-600";
      case "cancelled":
        return "text-red-600";
      default:
        return "text-muted-foreground";
    }
  };

  return (
    <div className="pt-24 pb-16" data-testid="account-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="mb-10">
          <p className="label-style mb-2">{t('my')}</p>
          <h1 className="section-title">{t('account')}</h1>
        </div>

        {/* Profile Section */}
        <div className="bg-secondary p-6 mb-10">
          <div className="flex items-center gap-4 mb-4">
            {/* Profile Image with Upload */}
            <div className="relative group">
              <div 
                className="h-20 w-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center overflow-hidden cursor-pointer border-4 border-white shadow-lg"
                onClick={() => fileInputRef.current?.click()}
              >
                {user?.profile_image ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_image}`}
                    alt={user?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="h-10 w-10" />
                )}
                {uploadingImage && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
                    <Loader2 className="h-6 w-6 text-white animate-spin" />
                  </div>
                )}
              </div>
              
              {/* Camera overlay on hover */}
              <div 
                className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="h-6 w-6 text-white" />
              </div>
              
              {/* Delete button if image exists */}
              {user?.profile_image && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteImage();
                  }}
                  className="absolute -bottom-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors"
                  title={t('removeProfileImage')}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="profile-image-input"
              />
            </div>
            
            <div>
              <p className="text-sm text-muted-foreground mb-1">Hello,</p>
              <h2 className="font-heading font-bold text-xl">{user?.name}</h2>
              <p className="text-muted-foreground">{user?.email}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('clickPhotoToChange')}</p>
            </div>
          </div>
          <div className="flex gap-4">
            {user?.is_admin && (
              <Link to="/admin">
                <Button variant="outline" className="rounded-full uppercase tracking-widest text-xs">
                  {t('adminPanel')}
                </Button>
              </Link>
            )}
            <Button
              variant="outline"
              onClick={handleLogout}
              className="rounded-full uppercase tracking-widest text-xs"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('logout')}
            </Button>
          </div>
        </div>

        {/* Horizontal Tabs - Black Pills */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab('details')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all bg-black text-white ${
                activeTab === 'details'
                  ? 'ring-2 ring-offset-2 ring-black'
                  : 'opacity-70 hover:opacity-100'
              }`}
              data-testid="tab-details"
            >
              <User className="h-4 w-4" />
              My Details & Orders
              {orders.length > 0 && (
                <span className="px-1.5 py-0.5 bg-white/20 text-white text-xs rounded-full font-medium">
                  {orders.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('rewards')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all bg-black text-white ${
                activeTab === 'rewards'
                  ? 'ring-2 ring-offset-2 ring-black'
                  : 'opacity-70 hover:opacity-100'
              }`}
              data-testid="tab-rewards"
            >
              <Gift className="h-4 w-4" />
              Rewards & Loyalty
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all bg-black text-white ${
                activeTab === 'notifications'
                  ? 'ring-2 ring-offset-2 ring-black'
                  : 'opacity-70 hover:opacity-100'
              }`}
              data-testid="tab-notifications"
            >
              <Bell className="h-4 w-4" />
              Notifications & Messages
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full font-bold">
                  {notifications.filter(n => !n.read).length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('topics')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all bg-black text-white ${
                activeTab === 'topics'
                  ? 'ring-2 ring-offset-2 ring-black'
                  : 'opacity-70 hover:opacity-100'
              }`}
              data-testid="tab-topics"
            >
              <MessageSquare className="h-4 w-4" />
              Your Topics
              {userPosts.length > 0 && (
                <span className="px-1.5 py-0.5 bg-white/20 text-white text-xs rounded-full font-medium">
                  {userPosts.length}
                </span>
              )}
            </button>
            <Link
              to="/mini-owners-club?view=forsale"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all bg-black text-white opacity-70 hover:opacity-100"
              data-testid="tab-sell"
            >
              <Store className="h-4 w-4" />
              {t('sell')}
            </Link>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
          <div data-testid="details-content" className="space-y-6">
            
            {/* Section 1: My Details */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg">My Details</h3>
                </div>
                {!editingDetails ? (
                  <Button
                    size="sm"
                    onClick={() => setEditingDetails(true)}
                    className="rounded-full bg-black text-white hover:bg-gray-800"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingDetails(false)}
                      className="rounded-full"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveCustomerDetails}
                      disabled={savingDetails}
                      className="rounded-full bg-black text-white hover:bg-gray-800"
                    >
                      {savingDetails ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                    </Button>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">First Name</label>
                    {editingDetails ? (
                      <input
                        type="text"
                        value={customerDetails.firstName}
                        onChange={(e) => setCustomerDetails({...customerDetails, firstName: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="Enter first name"
                      />
                    ) : (
                      <p className="text-foreground font-medium">{customerDetails.firstName || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Last Name</label>
                    {editingDetails ? (
                      <input
                        type="text"
                      value={customerDetails.lastName}
                      onChange={(e) => setCustomerDetails({...customerDetails, lastName: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="Enter last name"
                    />
                  ) : (
                    <p className="text-foreground font-medium">{customerDetails.lastName || '-'}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Address</label>
                  {editingDetails ? (
                    <input
                      type="text"
                      value={customerDetails.address}
                      onChange={(e) => setCustomerDetails({...customerDetails, address: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="Enter street address"
                    />
                  ) : (
                    <p className="text-foreground font-medium">{customerDetails.address || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">City</label>
                  {editingDetails ? (
                    <input
                      type="text"
                      value={customerDetails.city}
                      onChange={(e) => setCustomerDetails({...customerDetails, city: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="Enter city"
                    />
                  ) : (
                    <p className="text-foreground font-medium">{customerDetails.city || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Postcode</label>
                  {editingDetails ? (
                    <input
                      type="text"
                      value={customerDetails.postcode}
                      onChange={(e) => setCustomerDetails({...customerDetails, postcode: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="Enter postcode"
                    />
                  ) : (
                    <p className="text-foreground font-medium">{customerDetails.postcode || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Country</label>
                  {editingDetails ? (
                    <select
                      value={customerDetails.country}
                      onChange={(e) => setCustomerDetails({...customerDetails, country: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white"
                    >
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="United States">United States</option>
                      <option value="Germany">Germany</option>
                      <option value="France">France</option>
                      <option value="Spain">Spain</option>
                      <option value="Italy">Italy</option>
                      <option value="Netherlands">Netherlands</option>
                      <option value="Belgium">Belgium</option>
                      <option value="Ireland">Ireland</option>
                      <option value="Australia">Australia</option>
                      <option value="Canada">Canada</option>
                    </select>
                  ) : (
                    <p className="text-foreground font-medium">{customerDetails.country || '-'}</p>
                  )}
                </div>
                </div>
              </div>
            </div>

            {/* Section 2: Payment Methods */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center">
                    <Wallet className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg">Payment Methods</h3>
                </div>
              </div>

              {/* Wallet Tabs */}
              <div className="flex gap-2 mb-5">
                <button
                  onClick={() => setWalletTab('cards')}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    walletTab === 'cards'
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <CreditCard className="h-4 w-4 inline mr-2" />
                  Saved Cards
                </button>
                <button
                  onClick={() => setWalletTab('preferences')}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    walletTab === 'preferences'
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <Settings className="h-4 w-4 inline mr-2" />
                  Preferences
                </button>
                <button
                  onClick={() => setWalletTab('add')}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    walletTab === 'add'
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <Plus className="h-4 w-4 inline mr-2" />
                  Add New
                </button>
              </div>

              {/* Security Notice */}
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                <Shield className="h-5 w-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm text-green-800 font-medium">Your payment details are secure</p>
                  <p className="text-xs text-green-600">All payment information is encrypted and securely stored.</p>
                </div>
                <Lock className="h-4 w-4 text-green-600 flex-shrink-0" />
              </div>

              {/* Saved Cards Tab */}
              {walletTab === 'cards' && (
                <div className="space-y-3">
                  {paymentMethods.length === 0 ? (
                    <div className="bg-white rounded-xl p-6 text-center border border-gray-200">
                      <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-300" strokeWidth={1} />
                      <p className="text-gray-500 mb-4">No saved payment methods</p>
                      <Button 
                        variant="outline" 
                        className="rounded-full"
                        onClick={() => setWalletTab('add')}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Payment Method
                      </Button>
                    </div>
                  ) : (
                    paymentMethods.map((card) => (
                      <div 
                        key={card.id}
                        className={`bg-white rounded-xl p-4 border-2 ${card.isDefault ? 'border-black' : 'border-gray-200'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-8 bg-gradient-to-br from-gray-700 to-gray-900 rounded flex items-center justify-center">
                              <CreditCard className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium capitalize">{card.type}</span>
                                <span className="text-gray-500">•••• {card.last4}</span>
                                {card.isDefault && (
                                  <span className="px-2 py-0.5 bg-black text-white text-xs rounded-full">Default</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">
                                Expires {card.expMonth.toString().padStart(2, '0')}/{card.expYear}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!card.isDefault && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDefaultPaymentMethod(card.id)}
                                className="text-xs"
                              >
                                Set Default
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingCard(card.id)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removePaymentMethod(card.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Preferences Tab */}
              {walletTab === 'preferences' && (
                <div className="bg-white rounded-xl p-5 border border-gray-200">
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Payment Preferences
                  </h4>
                    
                  {/* Use Default at Checkout */}
                  <div className="flex items-center justify-between py-4 border-b border-gray-100">
                    <div>
                      <p className="font-medium text-sm">Use default method at checkout</p>
                      <p className="text-xs text-gray-500">Automatically select your default payment method</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={paymentPreferences.useDefaultAtCheckout}
                        onChange={(e) => {
                          setPaymentPreferences({...paymentPreferences, useDefaultAtCheckout: e.target.checked});
                          toast.success('Preference updated');
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                    </label>
                  </div>

                  {/* Save Payment Details */}
                  <div className="flex items-center justify-between py-4 border-b border-gray-100">
                    <div>
                      <p className="font-medium text-sm">Save payment details at checkout</p>
                      <p className="text-xs text-gray-500">Allow saving new cards for faster checkouts</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={paymentPreferences.savePaymentDetails}
                        onChange={(e) => {
                          setPaymentPreferences({...paymentPreferences, savePaymentDetails: e.target.checked});
                          toast.success('Preference updated');
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                    </label>
                  </div>

                  {/* Default Payment Method Selection */}
                  <div className="py-4">
                    <p className="font-medium text-sm mb-3">Default payment method</p>
                    <div className="space-y-2">
                      {paymentMethods.map((card) => (
                        <label 
                          key={card.id}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border-2 transition-all ${
                            card.isDefault ? 'border-black bg-gray-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                        >
                            <input
                              type="radio"
                              name="defaultCard"
                              checked={card.isDefault}
                              onChange={() => setDefaultPaymentMethod(card.id)}
                              className="w-4 h-4 accent-black"
                            />
                            <CreditCard className="h-4 w-4 text-gray-600" />
                            <span className="capitalize">{card.type}</span>
                            <span className="text-gray-500">•••• {card.last4}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                </div>
              )}

              {/* Add New Card Tab */}
              {walletTab === 'add' && (
                <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-300" strokeWidth={1} />
                  <h4 className="font-medium mb-2">Add a New Payment Method</h4>
                  <p className="text-sm text-gray-500 mb-6">
                    Payment methods are managed through Stripe. Add a card during checkout and it will be saved here.
                  </p>
                  <Link to="/products">
                    <Button className="rounded-full bg-black text-white hover:bg-gray-800">
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      Continue Shopping
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Section 3: Your Orders */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center">
                    <Package className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg">Your Orders</h3>
                </div>
                <Link 
                  to="/cart"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full bg-black text-white hover:bg-gray-800 transition-colors"
                  data-testid="view-cart-link"
                >
                  <ShoppingCart className="h-4 w-4" />
                  View Cart
                </Link>
              </div>

              {/* Order Tabs */}
              <div className="flex gap-2 mb-5">
                <button
                  onClick={() => setOrderTab('history')}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    orderTab === 'history'
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                  data-testid="order-history-tab"
                >
                  <History className="h-4 w-4 inline mr-2" />
                  Order History
                </button>
                <button
                  onClick={() => setOrderTab('buyagain')}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    orderTab === 'buyagain'
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                  data-testid="buy-again-tab"
                >
                  <RotateCcw className="h-4 w-4 inline mr-2" />
                  Buy Again
                </button>
              </div>

              {/* Order History Tab Content */}
              {orderTab === 'history' && (
                <>
                  {loading ? (
                    <div className="space-y-3">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-24 bg-white animate-pulse rounded-xl" />
                      ))}
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                      <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" strokeWidth={1} />
                      <p className="text-gray-500 mb-4">{t('noOrdersYet')}</p>
                      <Link to="/products">
                        <Button className="rounded-full px-8 bg-black text-white hover:bg-gray-800">
                          {t('startShopping')}
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {orders.map((order) => (
                        <div
                          key={order.id}
                          className="bg-white rounded-xl p-4 border border-gray-200"
                          data-testid={`order-${order.id}`}
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {t('order')} #{order.id.slice(0, 8).toUpperCase()}
                              </p>
                              <p className="text-sm text-gray-500">
                                {new Date(order.created_at).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric"
                                })}
                              </p>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                                order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                order.status === 'completed' ? 'bg-green-100 text-green-700' :
                                order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {order.status}
                              </span>
                              <span className="price-font font-bold text-lg">
                                £{order.total.toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap gap-2">
                              {order.items.slice(0, 3).map((item, idx) => (
                                <img
                                  key={idx}
                                  src={item.image_url || "https://images.unsplash.com/photo-1766602110905-e979239fd291?w=200"}
                                  alt={item.name}
                                  className="w-12 h-12 object-cover bg-gray-100 rounded-lg"
                                />
                              ))}
                              {order.items.length > 3 && (
                                <div className="w-12 h-12 bg-gray-100 flex items-center justify-center text-sm text-gray-500 rounded-lg">
                                  +{order.items.length - 3}
                                </div>
                              )}
                            </div>
                            <Button
                              onClick={() => handleDownloadInvoice(order.id)}
                              disabled={downloadingInvoice[order.id]}
                              variant="outline"
                              size="sm"
                              className="rounded-full text-xs"
                              data-testid={`download-invoice-${order.id}`}
                            >
                              {downloadingInvoice[order.id] ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <Download className="h-4 w-4 mr-1" />
                                  Invoice
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Buy Again Tab Content */}
              {orderTab === 'buyagain' && (
                <div>
                  {orders.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                      <RotateCcw className="h-12 w-12 mx-auto mb-4 text-gray-300" strokeWidth={1} />
                      <p className="text-gray-500 mb-4">No previous orders to reorder</p>
                      <Link to="/products">
                        <Button className="rounded-full px-8 bg-black text-white hover:bg-gray-800">
                          Start Shopping
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {orders
                        .flatMap(order => order.items)
                        .filter((item, index, self) => 
                          index === self.findIndex(i => i.product_id === item.product_id)
                        )
                        .slice(0, 12)
                        .map((item, idx) => (
                          <div 
                            key={`${item.product_id}-${idx}`}
                            className="bg-white rounded-xl p-4 border border-gray-200 hover:border-gray-300 transition-colors"
                          >
                            <Link to={`/product/${item.product_id}`}>
                              <div className="aspect-square mb-3 bg-gray-50 rounded-lg overflow-hidden">
                                <img
                                  src={item.image_url || "https://images.unsplash.com/photo-1766602110905-e979239fd291?w=200"}
                                  alt={item.name}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                              <h4 className="font-medium text-sm truncate mb-1">{item.name}</h4>
                              <p className="text-sm text-gray-500 mb-3">£{item.price?.toFixed(2) || '0.00'}</p>
                            </Link>
                            <Button
                              size="sm"
                              className="w-full rounded-full bg-black text-white hover:bg-gray-800"
                              disabled={addingToCart[item.product_id]}
                              onClick={async () => {
                                setAddingToCart(prev => ({ ...prev, [item.product_id]: true }));
                                try {
                                  const result = await addToCart(item.product_id, 1);
                                  if (result?.success) {
                                    toast.success(`${item.name} added to cart`);
                                  } else {
                                    toast.error(result?.message || 'Failed to add to cart');
                                  }
                                } catch (error) {
                                  toast.error('Failed to add to cart');
                                } finally {
                                  setAddingToCart(prev => ({ ...prev, [item.product_id]: false }));
                                }
                              }}
                              data-testid={`buy-again-${item.product_id}`}
                            >
                              {addingToCart[item.product_id] ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <ShoppingCart className="h-4 w-4 mr-2" />
                                  Add to Cart
                                </>
                              )}
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'rewards' && (
          <div data-testid="rewards-content">
            <button
              onClick={() => setActiveTab('details')}
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Account
            </button>
            <LoyaltyCard />
          </div>
        )}

        {activeTab === 'notifications' && (
          <div data-testid="notifications-content">
            <button
              onClick={() => setActiveTab('details')}
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Account
            </button>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-medium text-lg">Notifications & Messages</h3>
              {notifications.filter(n => !n.read).length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="mark-all-read-btn"
                >
                  <Check className="h-3 w-3 mr-1" />
                  Mark all as read
                </Button>
              )}
            </div>

            {/* Notification Sub-tabs */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setNotificationTab('all')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-2 ${
                  notificationTab === 'all'
                    ? 'bg-black text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                data-testid="all-notifications-tab"
              >
                <Bell className="h-4 w-4" />
                All
                {notifications.filter(n => !n.read && n.type !== 'private_message').length > 0 && (
                  <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full font-bold">
                    {notifications.filter(n => !n.read && n.type !== 'private_message').length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setNotificationTab('inbox')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-2 ${
                  notificationTab === 'inbox'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                data-testid="inbox-tab"
              >
                <Mail className="h-4 w-4" />
                Inbox
                {notifications.filter(n => !n.read && n.type === 'private_message').length > 0 && (
                  <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full font-bold">
                    {notifications.filter(n => !n.read && n.type === 'private_message').length}
                  </span>
                )}
              </button>
            </div>

            {/* All Notifications Tab */}
            {notificationTab === 'all' && (
              <>
                {loadingNotifications ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-16 bg-secondary animate-pulse rounded-lg" />
                    ))}
                  </div>
                ) : notifications.filter(n => n.type !== 'private_message').length === 0 ? (
                  <div className="text-center py-12 bg-secondary/50 rounded-lg">
                    <Bell className="h-12 w-12 mx-auto mb-3 text-muted-foreground" strokeWidth={1} />
                    <p className="text-muted-foreground mb-2">{t('noNotifications')}</p>
                    <p className="text-sm text-muted-foreground">
                      You'll receive updates when someone likes or comments on your posts
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="notifications-list">
                    {notifications.filter(n => n.type !== 'private_message').map((notification) => (
                      <div
                        key={notification.id}
                        className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                          notification.read 
                            ? 'bg-white border-gray-200' 
                            : 'bg-blue-50 border-blue-200'
                        }`}
                        data-testid={`notification-${notification.id}`}
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-gray-100">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-sm text-gray-900">
                                {notification.title || notification.content}
                              </p>
                              <p className="text-sm text-gray-600 mt-0.5">{notification.message}</p>
                              <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              {!notification.read && (
                                <button
                                  onClick={() => markNotificationAsRead(notification.id)}
                                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                                  title="Mark as read"
                                >
                                  <Check className="h-4 w-4 text-gray-500" />
                                </button>
                              )}
                              <button
                                onClick={() => deleteNotification(notification.id)}
                                className="p-1.5 hover:bg-red-100 rounded-full transition-colors"
                                title="Delete"
                              >
                                <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
                              </button>
                            </div>
                          </div>
                          {notification.link && (
                            <Link
                              to={notification.link}
                              className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                            >
                              View post <ChevronRight className="h-3 w-3" />
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Inbox Tab - Private Messages Only */}
            {notificationTab === 'inbox' && (
              <>
                {loadingNotifications ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-20 bg-secondary animate-pulse rounded-lg" />
                    ))}
                  </div>
                ) : notifications.filter(n => n.type === 'private_message').length === 0 ? (
                  <div className="text-center py-12 bg-secondary/50 rounded-lg">
                    <Mail className="h-12 w-12 mx-auto mb-3 text-muted-foreground" strokeWidth={1} />
                    <p className="text-muted-foreground mb-2">No messages yet</p>
                    <p className="text-sm text-muted-foreground">
                      When sellers or buyers message you, they'll appear here
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="inbox-list">
                    {notifications.filter(n => n.type === 'private_message').map((message) => (
                      <div
                        key={message.id}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          message.read 
                            ? 'bg-white border-gray-200 hover:border-gray-300' 
                            : 'bg-purple-50 border-purple-300 hover:border-purple-400'
                        }`}
                        data-testid={`message-${message.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                            {message.title?.charAt(0) || 'M'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-gray-900">
                                    {message.title || 'New Message'}
                                  </span>
                                  {!message.read && (
                                    <span className="px-2 py-0.5 bg-purple-500 text-white text-[10px] font-bold rounded-full">
                                      NEW
                                    </span>
                                  )}
                                </div>
                                {message.listing_title && (
                                  <p className="text-xs text-purple-600 font-medium mb-2">
                                    Re: {message.listing_title}
                                  </p>
                                )}
                                <div className="bg-gray-50 p-3 rounded-lg mb-2">
                                  <p className="text-sm text-gray-700">
                                    "{message.preview || message.message}"
                                  </p>
                                </div>
                                <p className="text-xs text-gray-400">{message.time}</p>
                              </div>
                              <div className="flex items-center gap-1">
                                {!message.read && (
                                  <button
                                    onClick={() => markNotificationAsRead(message.id)}
                                    className="p-2 hover:bg-purple-100 rounded-full transition-colors"
                                    title="Mark as read"
                                  >
                                    <Check className="h-4 w-4 text-purple-600" />
                                  </button>
                                )}
                                <button
                                  onClick={() => deleteNotification(message.id)}
                                  className="p-2 hover:bg-red-100 rounded-full transition-colors"
                                  title="Delete"
                                >
                                  <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
                                </button>
                              </div>
                            </div>
                            {message.link && (
                              <Link
                                to={message.link}
                                className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium mt-2"
                              >
                                View listing <ChevronRight className="h-3 w-3" />
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'topics' && (
          <div data-testid="topics-content">
            <button
              onClick={() => setActiveTab('details')}
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Account
            </button>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-medium text-lg">Your Forum Topics</h3>
              <Link 
                to="/mini-owners-club"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                View Forum <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            {loadingPosts ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 bg-secondary animate-pulse rounded-lg" />
                ))}
              </div>
            ) : userPosts.length === 0 ? (
              <div className="text-center py-12 bg-secondary/50 rounded-lg">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 text-muted-foreground" strokeWidth={1} />
                <p className="text-muted-foreground mb-2">You haven't created any topics yet</p>
                <Link to="/mini-owners-club">
                  <Button className="mt-2 rounded-full" size="sm">
                    Create Your First Topic
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-secondary/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">{userPosts.length}</div>
                    <div className="text-xs text-muted-foreground uppercase">Topics</div>
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">
                      {userPosts.reduce((acc, p) => acc + (p.like_count || 0), 0)}
                    </div>
                    <div className="text-xs text-muted-foreground uppercase">Likes</div>
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">
                      {userPosts.reduce((acc, p) => acc + (p.comment_count || 0), 0)}
                    </div>
                    <div className="text-xs text-muted-foreground uppercase">Replies</div>
                  </div>
                </div>

                {/* Topics List */}
                {userPosts.map((post) => (
                  <div 
                    key={post.id}
                    className="bg-secondary/50 rounded-lg p-4 hover:bg-secondary transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={`/mini-owners-club?post=${post.id}`}
                          className="font-medium text-foreground hover:text-primary truncate block"
                        >
                          {post.title}
                        </Link>
                        <p className="text-sm text-muted-foreground truncate mt-1">{post.content}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="px-2 py-0.5 bg-gray-200 rounded-full text-gray-600">
                            {post.category || "General"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="h-3 w-3" /> {post.like_count || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="h-3 w-3" /> {post.comment_count || 0}
                          </span>
                          <span>{formatDate(post.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/mini-owners-club?edit=${post.id}`}
                          className="p-2 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDeletePost(post.id)}
                          className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Account;
